import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SpaceID } from '@playq/octopus-common';
import { QuerySpacesResponse, SpacesSort } from '@playq/octopus2-apps';
import { GenericFailure } from '@playq/services-shared';

import { experimentsQueryKeys } from '/api/hooks/experimentsService';
import { flowQueryKeys } from '/api/hooks/flows';
import { gameEventsQueryKeys } from '/api/hooks/gameEventsService';
import { queryBulkPromotionsKeys } from '/api/hooks/promotions';
import { spacesServiceQueryKeys, useSpaceDelete, useSpacesQuery } from '/api/hooks/spacesService';
import { PersistedQueryKeys } from '/common/models';
import { snackbarService } from '/common/snackbarService';
import { usePersistedQuery } from '/hooks/usePersistedQuery';
import { activeSpaceToolkit, authToolkit } from '/store';
import { IUseQueryOptionsExtended } from '/api/service-hooks';

import { useSpacesListPromotionsActions } from './components/useSpacesListPromotionsActions';
import { ISpacesQuery } from './types';

export const useSpacesList = (
  initialQuery?: ISpacesQuery,
  options?: IUseQueryOptionsExtended<QuerySpacesResponse | undefined, GenericFailure | Error>
) => {
  const firstRender = useRef(false);
  const [query, setQuery] = usePersistedQuery(PersistedQueryKeys.SpacesList, SpacesSort, initialQuery);
  const [entitiesProcessing, setEntitiesProcessing] = useState<{ [id: string]: boolean }>({});
  const isAdmin = useSelector(authToolkit.selectors.isAdmin);
  const activeSpace = useSelector(activeSpaceToolkit.selectors.activeSpace);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const setEntityProcessing = useCallback(
    (id: string, isProcessing: boolean) => setEntitiesProcessing((prevState) => ({ ...prevState, [id]: isProcessing })),
    []
  );

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (initialQuery) {
      setQuery(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuery, initialQuery?.filterBy, initialQuery?.iterator, initialQuery?.sortBy]);

  const {
    spaces,
    features,
    total,
    isLoading,
    refetch,
    tags: spacesQueryKey,
    mutateDeletedSpace,
  } = useSpacesQuery(query.iterator, query.sortBy, query.filterBy, options);

  const { mutate: deleteSpace } = useSpaceDelete({
    onMutate: (spaceID: SpaceID) => {
      setEntityProcessing(spaceID.id, true);
    },
    onSuccess: (_res, spaceID: SpaceID) => {
      mutateDeletedSpace(spaceID);
      queryClient.removeQueries({ queryKey: queryBulkPromotionsKeys });
      queryClient.removeQueries({ queryKey: experimentsQueryKeys });
      queryClient.removeQueries({ queryKey: flowQueryKeys });
      queryClient.removeQueries({ queryKey: gameEventsQueryKeys });

      if (activeSpace?.id.serialize() === spaceID.serialize()) {
        dispatch(activeSpaceToolkit.actions.reset());
      }

      snackbarService.success(`Space ${spaceID.id} has been deleted`);
    },
    onError: () => {
      refetch();
    },
    onSettled: (_res, _err, id) => {
      setEntityProcessing(id.id, false);
    },
    removeQueriesKeys: spacesServiceQueryKeys,
    excludedRemoveQueriesKeys: spacesQueryKey,
  });

  const { sandboxPromotions, livePromotions, clonePromotions, copyPromotions, updatePromotions, isSpaceProcessing } =
    useSpacesListPromotionsActions(entitiesProcessing);

  return {
    spaces,
    features,
    spacesQueryKey,
    total,
    query,
    pending: isLoading,
    activeSpace,
    entitiesProcessing,
    canWrite: isAdmin,
    sandboxPromotions,
    livePromotions,

    setQuery,
    deleteSpace,
    refresh: refetch,
    clonePromotions,
    copyPromotions,
    updatePromotions,
    isSpaceProcessing,
  };
};
